import { ReactNode, Ref, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import { colors } from '@karnott/colors';
import { ChevronIcon, KIcon } from '@karnott/icons';
import { pixelSize, pixelSpacing } from '@karnott/theme';
import { TooltipMessage } from '@karnott/tooltip';

type Props = {
  ref?: Ref<HTMLButtonElement>;
  id?: string;
  /** Label displayed when nothing is selected */
  title?: string;
  /** Bold label displayed when `hasSelection` is set, shown in a tooltip when truncated */
  selectionLabel?: ReactNode;
  hasSelection: boolean;
  isOpen: boolean;
  onClick: () => void;
  disabled?: boolean;
  error?: string | boolean;
  large?: boolean;
  thin?: boolean;
  Icon?: KIcon;
  tooltipOrientation?: 'top' | 'bottom' | 'left' | 'right';
};

/** The trigger button of the form popups: an icon, a title or a selection label, and a chevron */
export function PopupHandler({
  ref,
  id,
  title,
  selectionLabel,
  hasSelection,
  isOpen,
  onClick,
  disabled = false,
  error = false,
  large = false,
  thin = false,
  Icon,
  tooltipOrientation = 'top',
}: Props) {
  const [hovered, setHovered] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const selectionRef = useRef<HTMLSpanElement>(null);

  const setOver = (state: boolean) => {
    if (selectionRef.current && selectionRef.current.scrollWidth > selectionRef.current.offsetWidth) {
      setShowTooltip(state);
    }
  };

  let titleColor = colors('black');
  if (disabled) {
    titleColor = colors('grey', 'dark');
  } else if (hovered || isOpen) {
    titleColor = colors('white');
  } else if (error) {
    titleColor = colors('red');
  }

  return (
    <Handler
      type="button"
      ref={ref}
      id={id}
      onMouseEnter={() => !disabled && setHovered(true)}
      onMouseLeave={() => {
        setHovered(false);
        setOver(false);
      }}
      onMouseOver={() => setOver(true)}
      onClick={onClick}
      opened={isOpen}
      disabled={disabled}
      thin={thin}
      error={error}
      large={large}
      hasSelection={hasSelection}
    >
      {showTooltip && <TooltipMessage orientation={tooltipOrientation} message={selectionLabel} />}

      {Icon ? (
        <IconContainer className="popup-handler-icon">
          <Icon
            size={large ? 30 : 16}
            color={large ? 'currentColor' : titleColor}
            backgroundColor={titleColor}
            circled={large}
          />
        </IconContainer>
      ) : null}

      <TitleContainer titleColor={titleColor} thin={thin} large={large}>
        {hasSelection ? <Selection ref={selectionRef}>{selectionLabel}</Selection> : <Title>{title}</Title>}
      </TitleContainer>

      <RightIconContainer isOpen={isOpen}>
        <ChevronIcon size={18} color={titleColor} />
      </RightIconContainer>
    </Handler>
  );
}

const Handler = styled.button<{
  disabled?: boolean;
  opened?: boolean;
  thin?: boolean;
  error?: string | boolean;
  large: boolean;
  hasSelection: boolean;
}>`
  all: unset;
  flex: 1;
  display: flex;
  min-width: 0;
  gap: ${pixelSpacing('xSmall')};
  padding: ${({ large, thin }) =>
    thin ? `1px ${pixelSpacing('small')}` : large ? `0 ${pixelSpacing('medium')}` : pixelSpacing('small')};
  flex-direction: row;
  align-items: center;
  min-height: ${({ large }) => (large ? '56px' : 'unset')};
  border: solid 1px
    ${({ error, hasSelection, opened }) =>
      error ? colors('red') : hasSelection ? colors('black') : opened ? colors('grey') : colors('grey', 'light')};
  outline: ${({ hasSelection, opened, large, error, disabled }) =>
    hasSelection
      ? css`solid ${large ? 2 : 1.5}px ${colors(error ? 'red' : opened ? 'grey' : large ? 'bleen' : disabled ? 'grey' : 'black')}`
      : 'none'};
  outline-offset: -1px;
  border-radius: ${({ large }) => (large ? '10px' : pixelSpacing('xSmall'))};
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  --handler-bg-color: ${({ disabled, opened, error }) => {
    if (disabled) {
      return colors('grey', 'lighter');
    }
    if (opened) {
      return colors('grey');
    }
    if (error) {
      return colors('red', 300);
    }
    return colors('white');
  }};
  background-color: var(--handler-bg-color);
  transition: all 0.1s linear;

  &:hover {
    ${({ disabled }) =>
      !disabled &&
      css`
        --handler-bg-color: ${colors('grey', 'dark')};
        border-color: ${colors('grey', 'dark')};
        outline-color: ${colors('grey', 'dark')};
      `}
  }

  .popup-handler-icon {
    color: var(--handler-bg-color);
  }
`;

const IconContainer = styled.div`
  margin-right: ${pixelSpacing('small')};
`;

const TitleContainer = styled.div<{
  titleColor: string;
  thin?: boolean;
  large: boolean;
}>`
  position: relative;
  display: flex;
  flex: 1;
  flex-direction: row;
  color: ${({ titleColor }) => titleColor};
  box-sizing: border-box;
  overflow: hidden;
  font-size: ${({ thin, large }) => pixelSize(large ? 'large' : thin ? 'small' : 'regular')};
`;

const Title = styled.span`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
`;

const Selection = styled.span`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
  font-weight: bold;
`;

const RightIconContainer = styled.div<{
  isOpen: boolean;
}>`
  transition: transform 0.1s linear 0s;
  transform: ${({ isOpen }) => (isOpen ? `rotate(180deg)` : null)};
  display: flex;
  align-items: center;
  height: 15px;
`;
